import NPCContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/NPCContent";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import ChatConversation from "../model/ChatConversation";
import ChatModel from "../model/ChatModel";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import AssetLoadService from "../../../core/assetloading/AssetLoadService";
import ChatStatus from "../model/ChatStatus";
import ChatInteractionEvent from "../events/ChatInteractionEvent";
import OpenChatEvent from "../events/OpenChatEvent";
import AbstractMediator from "../../../core/ui/AbstractMediator";

export default class ChatContactItemMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {ChatModel} chatModel 
     * @param {RevisableLanguagePack} languagePack 
     * @param {AssetLibrary} assetLibrary 
     * @param {AssetLoadService} assetLoadService 
     */
    constructor(target, dispatcher, chatModel, languagePack, assetLibrary, assetLoadService) {
        super(target, dispatcher);

        this._chatModel = chatModel;
        this._languagePack = languagePack;
        this._assetLibrary = assetLibrary;
        this._assetLoadService = assetLoadService;

        this._npcId = this.getCustomData().npcId;

        /** @type {NPCContent} */
        this._npc = this.getCustomData().npc;
        /** @type {ChatConversation} */
        this._conversation = this.getCustomData().conversation;
    }

    initialize() {
        super.initialize();

        this._target.nameTextField.text = this._npc.getName() || this._npc.getNickname();
        this._target.titleTextField.text = this._npc.getRole();

        this._updateStatus();

        const newMessages = this._conversation.messages.reduce((prev, curr) => {
            if (!curr.isRead) {
                return prev + 1;
            }

            return prev;
        }, 0);
        const newMessagesText = newMessages.toString();
        this._target.newIndicator.gotoAndStop(newMessagesText.length > 1 ? 1 : 0);

        this._target.newIndicator.textField.text = newMessagesText;
        this._hasNewItems = newMessages > 0;
        this._target.newIndicator.visible = this._hasNewItems;

        this._itemButton = this.createButton(this._target, this._onClick.bind(this));

        const npcImage = this._npc.getImage();
        if (npcImage) {
            this._loadAndShowImage(`${npcImage}_small`, `assets/images/npcs/${npcImage}_small.png`);
        }

        this.registerEventListener(ChatInteractionEvent.CHAT_STATUS_CHANGED, this._updateStatus.bind(this));
    }

    _onClick() {
        const evt = new OpenChatEvent(OpenChatEvent.PERFORM, this._npcId);
        this.dispatch(evt.type, { data: evt });
    }

    _updateStatus() {
        switch (this._conversation.status) {
            case ChatStatus.ONLINE:
                this._target.statusTextField.text = this._languagePack.getText(`chat.status.available`);
                this._target.statusIndicator.gotoAndStop('online');
                break;
            case ChatStatus.TYPING:
                this._target.statusTextField.text = this._languagePack.getText(`chat.status.isTyping`);
                this._target.statusIndicator.gotoAndStop('online');
                break;
            case ChatStatus.OFFLINE:
                this._target.statusTextField.text = this._languagePack.getText(`chat.status.unavailable`);
                this._target.statusIndicator.gotoAndStop('offline');
                break;
            default:
                console.warn(`Unhandled status.`);
        }
    }

    _loadAndShowImage(id, url) {
        if (this._assetLibrary.hasAsset(id)) {
            this._onImageLoaded(id, false);
            return;
        }

        this._assetLoadService.lazyLoadImage(id, url, this._onImageLoaded.bind(this));
    }

    _onImageLoaded(assetId, animated = true) {
        const image = this._assetLibrary.getAsset(assetId);
        const bitmap = new createjs.Bitmap(image);
        this._target.imageContainer.addChild(bitmap);

        if (animated) {
            bitmap.alpha = 0;

            createjs.Tween.get(bitmap)
                .to({ alpha: 1 }, 150, createjs.Ease.sineInOut)
                .call(() => {
                    this._target.noPortraitThumb.visible = false;
                });
        } else {
            this._target.noPortraitThumb.visible = false;
        }
    }

    get skin() {
        return this._target;
    }

    get hasNewItems() {
        return this._hasNewItems;
    }
}